import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KeyRound, Lock, Settings, Shield } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/ui';
import { cn } from '@shared/lib/utils';
import { SUPPORTED_LANGUAGES } from '@shared/i18n/config/languages';
import { persistLanguage } from '@shared/i18n/lib/persist-locale';
import { useAuth } from '@entities/auth';
import { EnableTwoFactorModal } from '@features/auth/two-factor';
import { ChangePasswordModal } from '@features/auth/change-password';
import { ApiTokenModal } from '@features/auth/api-token';

interface SettingsMenuProps {
  iconClassName?: string;
}

export function SettingsMenu({ iconClassName }: SettingsMenuProps = {}) {
  const { t, i18n } = useTranslation('cabinet');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isEnable2faOpen, setIsEnable2faOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isApiTokenOpen, setIsApiTokenOpen] = useState(false);

  const handleLanguageChange = (value: string) => {
    void i18n.changeLanguage(value);
    persistLanguage(value);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 rounded-xl"
            aria-label={t('header.settings')}
          >
            <Settings className={cn(iconClassName ?? 'size-7')} />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>{t('header.language')}</DropdownMenuLabel>
          <div className="px-2 pb-2" onPointerDown={(event) => event.preventDefault()}>
            <Select value={i18n.language} onValueChange={handleLanguageChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_LANGUAGES.map((code) => (
                  <SelectItem key={code} value={code}>
                    {t(`header.languages.${code}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DropdownMenuItem onSelect={() => navigate('/security')} className="cursor-pointer gap-2">
            <Shield className="h-4 w-4" />
            {t('security.section')}
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={() => setIsChangePasswordOpen(true)}
            className="cursor-pointer gap-2"
          >
            <Lock className="h-4 w-4" />
            {t('header.changePassword')}
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={() => setIsApiTokenOpen(true)}
            className="cursor-pointer gap-2"
          >
            <KeyRound className="h-4 w-4" />
            {t('header.apiToken')}
          </DropdownMenuItem>

          {!user?.twoFactorEnabled && (
            <DropdownMenuItem
              onSelect={() => setIsEnable2faOpen(true)}
              className="cursor-pointer gap-2"
            >
              <Shield className="h-4 w-4" />
              {t('header.enable2fa')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <EnableTwoFactorModal open={isEnable2faOpen} onOpenChange={setIsEnable2faOpen} />
      <ChangePasswordModal open={isChangePasswordOpen} onOpenChange={setIsChangePasswordOpen} />
      <ApiTokenModal open={isApiTokenOpen} onOpenChange={setIsApiTokenOpen} />
    </>
  );
}
